import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static get targets() {
    return [
      'predictiveResults',
      'targetInput',
      'destinationUid',
      'lat',
      'lng',
      'results',
      'noResults',
      'form',
      'submitButton',
      'enabledIcon',
      'disabledIcon',
    ];
  }

  static values = {
    getUrl: String,
    defaultValue: String,
    placeholder: String,
  }

  connect() {
    this.targetInputTarget.setAttribute('hx-get', this.getUrlValue);
    this.targetInputTarget.value = this.defaultValueValue || '';
    this.targetInputTarget.setAttribute('placeholder', this.placeholderValue || '');

    this.hideNoResultsTimeout = null;
    this.initializeEventListeners();
    this.validSelection = true;

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleClickOutside);
  }

  handleSubmit(event) {
    // Allow submission if user has a valid selection OR typed >= 3 chars
    // (server-side geocode fallback handles unselected text queries)
    if (!this.validSelection) {
      const inputValue = (this.targetInputTarget.value || '').trim();
      if (inputValue.length < 3) {
        event.preventDefault();
        return false;
      }
    }
    return true;
  }

  initializeEventListeners() {
    this.element.addEventListener('htmx:afterOnLoad', this.handleAfterOnLoad.bind(this));
    this.element.addEventListener('htmx:afterSwap', this.handleAfterSwap.bind(this));
    this.formTarget.addEventListener('submit', this.handleSubmit.bind(this));
  }

  handleAfterOnLoad(event) {
    /* Handle the afterOnLoad event from htmx */

    const targetId = event.detail.target.id;
    // Support both desktop and mobile predictive results
    if ((targetId !== 'predictive-results' && targetId !== 'mobile-predictive-results') || !this.predictiveResultsTarget) return;

    if (this.targetInputTarget.value === '') {
      this.predictiveResultsTarget.classList.add('hidden');
      this.validSelection = true;
      // Make button valid when input is empty - with null checks
      if (this.hasDisabledIconTarget) {
        this.disabledIconTarget.classList.add('hidden');
      }
      if (this.hasEnabledIconTarget) {
        this.enabledIconTarget.classList.remove('hidden');
      }
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.classList.remove('bg-[#808080]');
        this.submitButtonTarget.classList.add('bg-[#0FEED7]');
      }
    } else {
      this.toggleResultsVisibility();
    }
  }

  toggleResultsVisibility() {
    /* Show the predictive results dropdown — both results and no-results states stay visible
       until the user selects a result, clears the input, or clicks outside. */
    clearTimeout(this.hideNoResultsTimeout);
    this.predictiveResultsTarget.classList.remove('hidden');
  }

  handleAfterSwap(event) {
    /* Handle the afterSwap event from htmx */
    const targetId = event.detail.target.id;
    if (targetId === 'predictive-results' || targetId === 'mobile-predictive-results') {
      this.addClickListenersToResults();
      this.validSelection = false;
      // Clear destination UUID and lat/lng when user types new text
      if (this.hasDestinationUidTarget) {
        this.destinationUidTarget.value = '';
      }
      if (this.hasLatTarget) {
        this.latTarget.value = '';
      }
      if (this.hasLngTarget) {
        this.lngTarget.value = '';
      }
      // Make button invalid - with null checks
      if (this.hasEnabledIconTarget) {
        this.enabledIconTarget.classList.add('hidden');
      }
      if (this.hasDisabledIconTarget) {
        this.disabledIconTarget.classList.remove('hidden');
      }
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.classList.remove('bg-[#0FEED7]');
        this.submitButtonTarget.classList.add('bg-[#808080]');
      }
    }
  }

  handleClickOutside(event) {
    /* Close dropdown when clicking outside the search bar */
    if (!this.hasPredictiveResultsTarget) return;
    if (this.predictiveResultsTarget.classList.contains('hidden')) return;

    const clickedInsideResults = this.predictiveResultsTarget.contains(event.target);
    const clickedInsideInput = this.targetInputTarget.contains(event.target);

    if (!clickedInsideResults && !clickedInsideInput) {
      this.predictiveResultsTarget.classList.add('hidden');
    }
  }

  addClickListenersToResults() {
    /* Add click event listeners to each result item */
    this.resultsTargets.forEach((item) => {
      item.addEventListener('click', (event) => {
        const resultType = item.getAttribute('data-type');
        const href = item.getAttribute('href');

        // For properties with href, let the browser navigate naturally
        if (resultType === 'property' && href) {
          // Don't prevent default - allow navigation
          return;
        }

        // For destinations and geocoded results, set the input value and mark as valid
        event.preventDefault();
        this.targetInputTarget.value = item.getAttribute('data-value');

        if (resultType === 'geocoded') {
          // Geocoded result: set lat/lng, clear destination UUID
          if (this.hasLatTarget) {
            this.latTarget.value = item.getAttribute('data-lat') || '';
          }
          if (this.hasLngTarget) {
            this.lngTarget.value = item.getAttribute('data-lng') || '';
          }
          if (this.hasDestinationUidTarget) {
            this.destinationUidTarget.value = '';
          }
        } else {
          // Destination result: set UUID, clear lat/lng
          if (this.hasDestinationUidTarget) {
            this.destinationUidTarget.value = item.getAttribute('data-uuid') || '';
          }
          if (this.hasLatTarget) {
            this.latTarget.value = '';
          }
          if (this.hasLngTarget) {
            this.lngTarget.value = '';
          }
        }
        this.validSelection = true;

        // Make button valid - with null checks
        if (this.hasDisabledIconTarget) {
          this.disabledIconTarget.classList.add('hidden');
        }
        if (this.hasEnabledIconTarget) {
          this.enabledIconTarget.classList.remove('hidden');
        }
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.classList.remove('bg-[#808080]');
          this.submitButtonTarget.classList.add('bg-[#0FEED7]');
        }
        this.predictiveResultsTarget.classList.add('hidden');
      });
    });
  }
}
